import { dom } from '@omegagrid/core';
import { msg } from "@omegagrid/localize";
import { FormAdapter, FormAlert, FormContainerOptions, FormControlProperties, FormControlType, FormValues } from '@omegagrid/form';
import rp from '@reporting/rp-app-host';
import { Accounting, AccountingRights, Application } from '@reporting/rp-types/core';

/**
 * 'company' = the application serves one company; 'group' = every company of the current accounting
 * group (the company it is registered from is only its home) — group administrators only.
 */
export type ClientCredentialsFormVariant = 'company' | 'group';

/** one or more http(s) URIs, comma-separated */
const REDIRECT_URI_PATTERN = /^(https?:\/\/\S+(\s*,\s*https?:\/\/\S+)*)?$/;

export class ClientCredentialsFormAdapter extends FormAdapter {

	/**
	 * @param variant the binding a new registration gets
	 * @param application the registration to edit instead. The client id, the secret, the company,
	 *   the scope and the binding are fixed once issued (as everywhere OAuth clients are registered);
	 *   only the redirect URIs change — Copilot Studio, for one, tells its callback URL only once the
	 *   connector exists.
	 */
	constructor(
		private variant: ClientCredentialsFormVariant,
		private application: Application = null,
	) {
		super();
	}

	getColumns() {
		return [
			{width: 140},
			{width: null},
		];
	}

	/** the rights the caller holds, as list items — a registration may not carry more */
	private get scopeOptions() {
		const scopes = Object.keys(AccountingRights)
			.filter(r => rp.userProvider.hasRight((AccountingRights as any)[r]))
			.map(r => ({ k: (AccountingRights as any)[r], v: r }));
		scopes.sort((a, b) => Number(b.k.startsWith('io.')) - Number(a.k.startsWith('io.')) || a.k.localeCompare(b.k));
		return scopes;
	}

	async getControls(): Promise<FormControlProperties[]> {
		return this.application ? this.getEditControls(this.application) : this.getRegistrationControls();
	}

	private async getRegistrationControls(): Promise<FormControlProperties[]> {
		let r = 0;
		const controls: FormControlProperties[] = [
			{row: r, col: 0, type: FormControlType.Label, value: msg('Name'), for: 'name'},
			{row: r++, col: 1, type: FormControlType.TextInput, key: 'name', required: true, pattern: /^.{0,200}$/},
		];

		if (this.variant === 'group') {
			// registered from the current company (its home); the tokens serve every company of the group
			controls.push(
				{row: r, col: 0, type: FormControlType.Label, value: msg('Companies')},
				{row: r++, col: 1, type: FormControlType.Label, value: msg('All companies of the current group')},
			);
		} else {
			// an application can only be created for a company the caller administers
			const accountings = await rp.ms('accounting').get<Accounting[]>(
				`accountings/${rp.accountingProvider.accounting.id_accounting_group}`,
				{ right: AccountingRights.Admin }
			);
			controls.push(
				{row: r, col: 0, type: FormControlType.Label, value: msg('Company'), for: 'accounting'},
				{row: r++, col: 1, type: FormControlType.List, key: 'accounting', required: true, value: rp.accountingProvider.a, content: {
					sourceData: accountings.map(a => ({ k: a.accounting, v: `${a.accounting} ${a.company}` })),
					options: { selectMode: 'single', autoClose: true, search: true }
				}},
			);
		}

		controls.push(
			{row: r, col: 0, type: FormControlType.Label, value: msg('Scope'), for: 'scopes'},
			{row: r++, col: 1, type: FormControlType.List, key: 'scopes', required: true, content: {
				sourceData: this.scopeOptions,
				options: { selectMode: 'multiple', autoClose: false, search: true }
			}},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Requires User'), for: 'requires_user'},
			{row: r++, col: 1, type: FormControlType.Checkbox, key: 'requires_user'},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Redirect URI'), for: 'redirect_uri'},
			{row: r++, col: 1, type: FormControlType.TextInput, key: 'redirect_uri', pattern: REDIRECT_URI_PATTERN},
		);
		return controls;
	}

	private getEditControls(app: Application): FormControlProperties[] {
		const names = new Map(Object.keys(AccountingRights).map(r => [(AccountingRights as any)[r] as string, r]));
		const scope = app.scope_array == null
			? msg('User rights')
			: app.scope_array.length
				? app.scope_array.map(s => names.get(s) ?? s).join(', ')
				: msg('No access');
		const company = `${app.accounting} ${app.company ?? ''}`.trim();

		let r = 0;
		return [
			{row: r, col: 0, type: FormControlType.Label, value: msg('Name'), for: 'name'},
			{row: r++, col: 1, type: FormControlType.TextInput, key: 'name', value: app.name, readOnly: true},
			{row: r, col: 0, type: FormControlType.Label, value: this.variant === 'group' ? msg('Companies') : msg('Company'), for: 'company'},
			{row: r++, col: 1, type: FormControlType.TextInput, key: 'company', readOnly: true,
				value: this.variant === 'group' ? `${msg('Whole group')} (${company})` : company},
			{row: r, col: 0, type: FormControlType.Label, value: 'Client ID', for: 'client_id'},
			{row: r++, col: 1, type: FormControlType.TextInput, key: 'client_id', value: app.client_id, readOnly: true},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Scope'), for: 'scope'},
			{row: r++, col: 1, type: FormControlType.TextInput, key: 'scope', value: scope, readOnly: true},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Requires User'), for: 'requires_user'},
			{row: r++, col: 1, type: FormControlType.Checkbox, key: 'requires_user', value: !!app.requires_user, readOnly: true},
			{row: r, col: 0, type: FormControlType.Label, value: msg('Redirect URI'), for: 'redirect_uri'},
			{row: r++, col: 1, type: FormControlType.TextInput, key: 'redirect_uri', value: app.redirect_uri ?? null, pattern: REDIRECT_URI_PATTERN},
		];
	}

	async save(values: Map<string|number, FormValues>): Promise<boolean|FormAlert[]> {
		const formValues = values.get(0);
		const redirectUri = (formValues['redirect_uri'] as string)?.trim() || null;

		if (this.application) {
			// the one editable field, changed in place on the server
			await rp.ms('accounting').put(`clientCredentials/${this.application.idz}`, { redirect_uri: redirectUri }, { accounting: this.application.accounting });
			return true;
		}

		if (!(formValues['scopes'] as unknown[])?.length) return false;

		const application = {
			name: formValues['name'] as string,
			scope: (formValues['scopes'] as any[][])?.map(([first]) => first).join(' '),
			requires_user: !!formValues['requires_user'],
			redirect_uri: redirectUri,
			group_wide: this.variant === 'group',
		};
		// a group-wide application is registered from the current company, its home
		const accounting = this.variant === 'group' ? rp.accountingProvider.a : formValues['accounting'] as number;
		const result = await rp.ms('accounting').post<Application>('clientCredentials', application, { accounting });

		this.showDialog(result.client_id, result.client_secret_plain);
		return true;
	}

	showDialog(client_id: string, client_secret_plain: string|null): void {
		const div = dom.createElement<HTMLDivElement>('div');
		div.style.margin = '15px';
		div.style.userSelect = 'text';
		const row = (label: string, value: string) => `
			<tr>
				<td><strong>${label}:</strong></td>
				<td>
					<og-button
						icon="copy"
						title="${msg('Copy to clipboard')}"
						onclick="navigator.clipboard.writeText('${value}');">
					</og-button>
				</td>
				<td>${value}</td>
			</tr>
		`;

		// the secret is minted for every registration and shown exactly once: it is stored hashed
		div.innerHTML = `
			<table>
				${row('Client ID', client_id)}
				${client_secret_plain ? row('Client Secret', client_secret_plain) : ''}
			</table>
			${client_secret_plain ? `
				<br/>
				⚠️ ${msg('Please copy and store the Client Secret now.')}
				${msg('For security reasons, you will not be able to view it again.')}
			` : ''}
		`;

		rp.host.bucket.openDialog({
			component: div,
			caption: msg('Client credentials created'),
			closable: true,
			width: 750,
			height: 250,
			resizable: true,
			location: 'center',
		});
	}

	getOptions(): FormContainerOptions {
		return {
			caption: null,
			buttonsPosition: 'bottom',
			alertsPosition: 'bottom',
			buttons: ["save", "close"]
		};
	}

};
